import { useState } from 'react';
import { supabase } from './supabaseClient';
import AnvilLogo from './assets/anvil.png';

import GymPlannerIcon from './assets/Gym Planner Default.png';

import WorkoutHistoryIcon from './assets/Workout History Default.png';


import BullseyeIcon from './assets/Bullseye Default.png';

export default function AuthForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (isSigningUp) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });

        if (error) {
          setError(error.message);
        } else {
          if (data.user && !data.session) {
            setMessage('Check your email for the confirmation link!');
          } else if (data.session) {
            onLogin(data.session.user);
          }
        }
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          setError(error.message);
        } else if (data.session) {
          onLogin(data.session.user);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setError(error.message);
      } else {
        setMessage('Check your email for the password reset link!');
        // Reset form after 3 seconds
        setTimeout(() => {
          setIsForgotPassword(false);
          setMessage('');
        }, 3000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Password reset error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        }
      });
      if (error) setError(error.message);
    } catch (err) {
      console.error(err);
      setError('Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="h-10 rounded-lg overflow-hidden flex items-center justify-center">
            <img src={AnvilLogo} alt="Iron Path Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">The Iron Path</h1>
          <p className="text-purple-300">Your personal gym companion</p>
        </div>

        {/* Auth Form */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-8 shadow-2xl">
          <h2 className="text-2xl font-bold mb-6 text-white text-center">
            {isForgotPassword ? 'Reset Password' : isSigningUp ? 'Create Account' : 'Welcome Back'}
          </h2>
          
          <form onSubmit={isForgotPassword ? handleForgotPassword : handleAuth} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Email</label>
              <input
                type="email"
                placeholder="your@email.com"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>
            
            {!isForgotPassword && (
              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">Password</label>
                <input
                  type="password"
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  disabled={loading}
                  autoComplete={isSigningUp ? "new-password" : "current-password"}
                />
                {isSigningUp && (
                  <p className="text-xs text-purple-300 mt-1">Minimum 6 characters</p>
                )}
              </div>
            )}

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3">
                <p className="text-red-200 text-sm">{error}</p>
              </div>
            )}
            
            {message && (
              <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-3">
                <p className="text-green-200 text-sm">{message}</p>
              </div>
            )}

            <button 
              type="submit"
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Processing...
                </div>
              ) : isForgotPassword ? 'Send Reset Link' : isSigningUp ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            {!isForgotPassword && (
              <>
                <button
                  type="button"
                  className="text-purple-300 hover:text-white transition-colors duration-200 text-sm"
                  onClick={() => {
                    setIsForgotPassword(true);
                    setError('');
                    setMessage('');
                  }}
                >
                  Forgot your password?
                </button>
                <br />
              </>
            )}
            
            <button
              type="button"
              className="text-purple-300 hover:text-white transition-colors duration-200"
              onClick={() => {
                if (isForgotPassword) {
                  setIsForgotPassword(false);
                } else {
                  setIsSigningUp(!isSigningUp);
                }
                setError('');
                setMessage('');
              }}
            >
              {isForgotPassword ? 'Back to sign in' : isSigningUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
            </button>

            {!isForgotPassword && (
              <>
                {/* Divider */}
                <div className="flex items-center my-4">
                  <div className="h-px bg-white/20 flex-1" />
                  <span className="px-3 text-xs uppercase tracking-wide text-purple-200">or</span>
                  <div className="h-px bg-white/20 flex-1" />
                </div>

                {/* Google OAuth */}
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full bg-white text-slate-900 py-3 rounded-lg font-semibold border border-white/30 hover:bg-purple-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  Continue with Google
                </button>
              </>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="mt-8 text-center">
          <div className="flex justify-center space-x-6 text-purple-300">
            <div className="flex items-center">
              <img src={WorkoutHistoryIcon} alt="Diet" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
              <span className="text-sm">Track Progress</span>
            </div>
            <div className="flex items-center">
              <img src={BullseyeIcon} alt="Diet" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
              <span className="text-sm">Set Goals</span>
            </div>
            <div className="flex items-center">
              <img src={GymPlannerIcon} alt="Diet" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
              <span className="text-sm">Achieve More</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}